import {
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import moment from 'moment';
import { createElement, useCallback, useMemo, useState } from 'react';

import { useLazyGetEmployeeWorkScheduleForDateQuery } from '@app/api/WorkSchedule';
import { INDIVIDUAL_CALENDAR_PATH } from '@app/constants';
import {
  useNavigatePreserveQueryParams,
  useNewUserAccessRights,
  useUserPrint,
} from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { isValidTime } from '@app/shared/components/TimeInput';

import { useCalendarContext } from '@pages/Calendar/context';
import type { Employee } from '@pages/Calendar/data';

import type { MenuProps } from 'antd';

type WorkScheduleActionMode = 'edit' | 'delete';

interface WorkScheduleModalState {
  isOpen: boolean;
  mode: WorkScheduleActionMode;
  initialTimeS: string;
  initialTimeE: string;
  initialWarningText?: string;
  workScheduleId: number;
}

interface BreakModalState {
  isOpen: boolean;
  date: string;
  initialTimeS: string;
  initialTimeE: string;
  mode: 'create';
}

interface UseEmployeeHeaderActionsParams {
  employee: Employee;
}

export const useEmployeeHeaderActions = ({
  employee,
}: UseEmployeeHeaderActionsParams) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [preparingAction, setPreparingAction] = useState<string | null>(null);
  const [workScheduleModalState, setWorkScheduleModalState] =
    useState<WorkScheduleModalState | null>(null);
  const [breakModalState, setBreakModalState] =
    useState<BreakModalState | null>(null);
  const { navigatePreserveQueryParams } = useNavigatePreserveQueryParams();
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();
  const { refreshCalendarData, selectedDateMoment } = useCalendarContext();
  const {
    userAccessRights: {
      WorkSchedules: {
        IsEdit,
        IsEditPast,
        IsDelete,
        IsDeletePast,
        IsCreateBreak,
      },
    },
  } = useNewUserAccessRights();
  const [getEmployeeWorkScheduleForDate] =
    useLazyGetEmployeeWorkScheduleForDateQuery();
  const hasActiveWorkday = employee.hasActiveWorkday === true;

  const employeeId = useMemo(() => Number(employee.id), [employee.id]);

  const isPastDate = useMemo(
    () => selectedDateMoment.isBefore(moment(), 'day'),
    [selectedDateMoment]
  );

  const canEditSchedule = useMemo(
    () => hasActiveWorkday && (isPastDate ? IsEditPast : IsEdit),
    [IsEdit, IsEditPast, hasActiveWorkday, isPastDate]
  );

  const canDeleteSchedule = useMemo(
    () => hasActiveWorkday && (isPastDate ? IsDeletePast : IsDelete),
    [IsDelete, IsDeletePast, hasActiveWorkday, isPastDate]
  );

  const extractTimeLabel = useCallback((value?: string | null) => {
    if (!value) {
      return undefined;
    }

    if (value.includes('T')) {
      const parsedValue = dayjs(value);

      if (parsedValue.isValid()) {
        return parsedValue.format('HH:mm');
      }
    }

    return value.slice(0, 5);
  }, []);

  // Время для полей ЧЧ:ММ модалки; если из расписания его не разобрать —
  // берём рабочие часы из колонки, иначе оставляем поле пустым.
  const resolveInitialTime = useCallback(
    (value: string | null | undefined, fallback: string) => {
      const timeLabel = extractTimeLabel(value);

      if (isValidTime(timeLabel)) {
        return timeLabel;
      }

      return isValidTime(fallback) ? fallback : '';
    },
    [extractTimeLabel]
  );

  const getPreparedTimeRange = useCallback(
    (timeS?: string | null, timeE?: string | null) => ({
      initialTimeS: resolveInitialTime(timeS, employee.workStart),
      initialTimeE: resolveInitialTime(timeE, employee.workEnd),
    }),
    [employee.workEnd, employee.workStart, resolveInitialTime]
  );

  const selectedDateString = useMemo(
    () => selectedDateMoment.format('YYYY-MM-DD'),
    [selectedDateMoment]
  );

  const loadWorkScheduleForCurrentDate = useCallback(async () => {
    if (!ActiveBranchId || !Number.isFinite(employeeId)) {
      return null;
    }

    const result = await getEmployeeWorkScheduleForDate({
      ActiveBranchId,
      employeeId,
      date: selectedDateString,
    }).unwrap();

    if (!result.WorkSchedule) {
      showNotification(
        'У исполнителя нет рабочего графика на этот день. Сначала добавьте рабочий график.'
      );
      return null;
    }

    return result;
  }, [
    employeeId,
    getEmployeeWorkScheduleForDate,
    ActiveBranchId,
    selectedDateString,
    showNotification,
  ]);

  const handleOpenIndividualCalendar = useCallback(() => {
    if (!Number.isFinite(employeeId)) {
      return;
    }

    const dateS = selectedDateMoment.format('YYYY-MM-DD');

    navigatePreserveQueryParams(
      `${INDIVIDUAL_CALENDAR_PATH}?employeeId=${employeeId}&dateS=${dateS}`
    );
    setIsDropdownOpen(false);
  }, [employeeId, navigatePreserveQueryParams, selectedDateMoment]);

  const handleOpenWorkScheduleModal = useCallback(
    async (mode: WorkScheduleActionMode) => {
      setIsDropdownOpen(false);

      if (!hasActiveWorkday) {
        return;
      }

      if (!ActiveBranchId || !Number.isFinite(employeeId)) {
        return;
      }

      setPreparingAction(mode);

      try {
        const result = await loadWorkScheduleForCurrentDate();

        if (!result?.WorkSchedule) return;

        const timeRange = getPreparedTimeRange(
          result.WorkSchedule.TimeS,
          result.WorkSchedule.TimeE
        );

        setWorkScheduleModalState({
          isOpen: true,
          mode,
          initialTimeS: timeRange.initialTimeS,
          initialTimeE: timeRange.initialTimeE,
          initialWarningText:
            mode === 'edit'
              ? result.WarningForEdit ?? undefined
              : result.WarningForDelete ?? undefined,
          workScheduleId: result.WorkSchedule.Id,
        });
      } catch (error) {
        showNotification(error);
      } finally {
        setPreparingAction(null);
      }
    },
    [
      employeeId,
      getPreparedTimeRange,
      hasActiveWorkday,
      loadWorkScheduleForCurrentDate,
      ActiveBranchId,
      showNotification,
    ]
  );

  const handleOpenBreakModal = useCallback(async () => {
    setIsDropdownOpen(false);

    if (!hasActiveWorkday) {
      return;
    }

    if (!ActiveBranchId || !Number.isFinite(employeeId)) {
      return;
    }

    setPreparingAction('break');

    try {
      const result = await loadWorkScheduleForCurrentDate();

      if (!result?.WorkSchedule) {
        return;
      }

      setBreakModalState({
        isOpen: true,
        date: selectedDateString,
        mode: 'create',
        ...getPreparedTimeRange(
          result.WorkSchedule.TimeS,
          result.WorkSchedule.TimeE
        ),
      });
    } catch (error) {
      showNotification(error);
    } finally {
      setPreparingAction(null);
    }
  }, [
    employeeId,
    getPreparedTimeRange,
    hasActiveWorkday,
    loadWorkScheduleForCurrentDate,
    ActiveBranchId,
    selectedDateString,
    showNotification,
  ]);

  const handleCloseWorkScheduleModal = useCallback(() => {
    setWorkScheduleModalState(null);
  }, []);

  const handleCloseBreakModal = useCallback(() => {
    setBreakModalState(null);
  }, []);

  const handleBreakModalSuccess = useCallback(() => {
    refreshCalendarData();
  }, [refreshCalendarData]);

  const items = useMemo<MenuProps['items']>(
    () => [
      ...(canEditSchedule
        ? [
            {
              key: 'edit-work-schedule',
              icon: createElement(EditOutlined),
              label: createElement(
                'span',
                {
                  'data-testid': `employee-header-menu-edit-${employee.id}`,
                },
                'Изменить расписание'
              ),
              onClick: () => {
                void handleOpenWorkScheduleModal('edit');
              },
            },
          ]
        : []),
      ...(canDeleteSchedule
        ? [
            {
              key: 'delete-work-schedule',
              icon: createElement(DeleteOutlined),
              danger: true,
              label: createElement(
                'span',
                { 'data-testid': `employee-header-menu-delete-${employee.id}` },
                'Отменить рабочий день'
              ),
              onClick: () => {
                void handleOpenWorkScheduleModal('delete');
              },
            },
          ]
        : []),
      ...(hasActiveWorkday && IsCreateBreak
        ? [
            {
              key: 'add-break',
              icon: createElement(PauseCircleOutlined),
              label: createElement(
                'span',
                { 'data-testid': `employee-header-menu-break-${employee.id}` },
                'Добавить перерыв'
              ),
              onClick: () => {
                void handleOpenBreakModal();
              },
            },
          ]
        : []),
      {
        key: 'individual-calendar',
        icon: createElement(CalendarOutlined),
        label: createElement(
          'span',
          { 'data-testid': `employee-header-menu-journal-${employee.id}` },
          'Индивидуальный журнал'
        ),
        onClick: handleOpenIndividualCalendar,
      },
    ],
    [
      IsCreateBreak,
      canDeleteSchedule,
      canEditSchedule,
      employee.id,
      hasActiveWorkday,
      handleOpenBreakModal,
      handleOpenIndividualCalendar,
      handleOpenWorkScheduleModal,
    ]
  );

  const handleDropdownOpenChange = useCallback((open: boolean) => {
    setIsDropdownOpen(open);
  }, []);

  return {
    isDropdownOpen,
    isPreparingAction: preparingAction !== null,
    items,
    breakModalState,
    employeeId,
    workScheduleModalState,
    handleDropdownOpenChange,
    handleBreakModalSuccess,
    handleCloseBreakModal,
    handleCloseWorkScheduleModal,
  };
};
